/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description User management utilities
 */
import axios from "axios";
import { FormikValues } from "formik";
import { useMemo } from "react";
import { useAppContext } from "./../context/AppCtxProvider";
import { useIdBySymbol } from "./../utils/model";
export type Group = {
  id: string;
  fullname: string;
  description: string;
  name: string;
  pretty_name: string;
  typename: string;
  users: any[];
  addFrom?: string;
};
type Role = {
  _id: string;
  name: string;
  symbol: string;
  type: string;
  meaGroups: string[];
};
export type RoleWithGroup = Role & {
  meaGroup?: Group;
};
type User = {
  id?: string;
  name: string;
  typename?: string;
  organization: string;
  telephone: string;
  email: string;
  ingroups: string;
};
/** Axios body may be `{ data: T[] }` or legacy `{ data: { data: T[] } }` */
function listFromMeaResponse(res: { data?: unknown } | null | undefined): any[] {
  const body = res?.data;
  if (Array.isArray(body)) {
    return body;
  }
  if (body && typeof body === "object" && "data" in body) {
    const inner = (body as { data: unknown }).data;
    if (Array.isArray(inner)) {
      return inner;
    }
    if (
      inner &&
      typeof inner === "object" &&
      "data" in inner &&
      Array.isArray((inner as { data: unknown }).data)
    ) {
      return (inner as { data: any[] }).data;
    }
  }
  return [];
}

const userGroupsName: Record<string, string> = {
  ROLE_UI_TOP_LEADERSHIP: "Top Leadership",
  ROLE_UI_EA_SAC_LEADERSHIP: "EA / SAC Leadership",
  ROLE_UI_GOVERNMENT_LEADERSHIP: "Government Leadership",
  ROLE_UI_DP2030_MANAGEMENT: "DP2030 Management",
  ROLE_UI_TEC_THEME_AND_PRIORITY_OWNER: "TEC Theme and Priority Owner",
  ROLE_UI_TEC_COORDINATOR: "TEC Coordinator",
  ROLE_UI_TEC_IT_ADMIN: "TEC IT/System Admin",
  ROLE_UI_GE_COORDINATOR: "Government Entity Coordinator",
};
const useUsermanagement = () => {
  const ctx = useAppContext();
  const { data: geMain } = useIdBySymbol(ctx.modelId, "GE_MAIN");
  const { data: sectorMain } = useIdBySymbol(ctx.modelId, "SECTOR_MAIN");
  const { data: themeMain } = useIdBySymbol(ctx.modelId, `${ctx.modelId}_MAIN`);
  const [modelId, geScoreCardId] = useMemo(() => {
    const id = geMain?.data[0]?.scorecardid;
    if (id) {
      const modelId = `${id.split(".")[0]}.${id.split(".")[1]}`;
      return [modelId, id];
    }
    return [undefined, undefined];
  }, [geMain]);
  const sectorScoreCardId = useMemo(() => {
    const id = sectorMain?.data[0]?.scorecardid || "";
    return id;
  }, [sectorMain]);
  const themeScoreCardId = useMemo(() => {
    const id = themeMain?.data[0]?.scorecardid || "";
    return id;
  }, [themeMain]);
  const client = axios.create({
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  });
  const fetchUsers = async (id?: string) => {
    if (id) {
      const users = await client.get(`/users/${id}`);
      return users.data;
    }
    const users = await client.get("/users");
    return users.data.data;
  };
  const fetchGroupsAndUsers = async () => {
    const usersRes = await client.get("/users");
    const groupsRes = await client.get("/usergroups");
    const usersList = listFromMeaResponse(usersRes);
    const groupsList = listFromMeaResponse(groupsRes);
    const groups: Group[] = [];
    groupsList.forEach((group: Group) => {
      const usersInGroup: any[] = [];
      usersList.forEach((user: User) => {
        const userGroups = (user.ingroups || "").split(",");
        if (userGroups.includes(group.id)) {
          usersInGroup.push(user);
        }
      });
      groups.push({ ...group, users: usersInGroup });
    });
    return { groups, users: usersList };
  };
  const fetchGroup = async (id: string) => {
    return client
      .get(`/usergroups/${id}`)
      .then((response) => {
        return response.data;
      })
      .catch((e: any) => {
        console.log({ e });
      });
  };
  const fetchRoleGroups = async (query = "roletype=wf&roletype=ui") => {
    const groups = await client.get(`/usergroups?${query}`);
    return groups.data.data;
  };
  const fetchUpAndWf = async () => {
    const data = await client.get("/roles");
    return data.data.data;
  };
  const createUser = async (payload: User & any) => {
    const response = await axios.post("/users", {
      ...payload,
    });
    return response;
  };
  const updateUser = async (id: string, payload: any) => {
    const response = await axios.put(`/users/${id}`, {
      ...payload,
    });
    return response;
  };
  const createGroup = async (
    name: string,
    description: string,
    members: any
  ) => {
    const response = await axios.post("/usergroups", {
      name,
      description,
      permissionlevels: "SC=0, UM=0, PO=1",
      licenses: "SC_USER=1",
    });
    await axios.put(`/usergroups/${response.data.data.id}`, {
      accessLevelParameters: {
        objectIds: modelId,
        accessLevel: -2,
      },
    });
    await Promise.all(
      members.map(async (member: { id: string; ingroups: string }) => {
        const userUpdateResponse = await updateUser(member.id, {
          ingroups: `${member.ingroups},${response.data.data.id}`,
        });
      })
    );
    return response.data;
  };
  const updateGroup = async (
    id: string,
    name: string,
    description: string,
    members: any[],
    accessLevelParameters?: {
      objectIds: string;
      accessLevel: number;
      userIds?: string;
    }
  ) => {
    const response = await axios.put(`/usergroups/${id}`, {
      name,
      description,
      accessLevelParameters,
    });
    await Promise.all(
      members.map(async (member: { id: string; ingroups: string }) => {
        let newGroupIds: string[] = [];
        const oldGroupIdsOfMember = (member.ingroups || "").split(",");
        if (oldGroupIdsOfMember.includes(id)) {
          newGroupIds = oldGroupIdsOfMember.filter((gId) => gId !== id);
        } else {
          newGroupIds = oldGroupIdsOfMember;
          newGroupIds.push(id);
        }
        await updateUser(member.id, { ingroups: newGroupIds.join(",") });
      })
    );
    return response.data;
  };
  const fetchuserGroup = async (id: string) => {
    return [
      { id: 1, name: "11111 Sadek" },
      { id: 2, name: "Eyad Hesham" },
      { id: 3, name: "Xyz Abc" },
      { id: 4, name: "Toni walker" },
    ];
  };
  const fetchUsersFromGroup = (id: string) => {
    return client
      .get(`/usergroups/${id}`, {})
      .then((response) => {
        return response.data.data.users || [];
      })
      .catch((e) => console.log(e));
  };
  const deleteUser = (id: string) => {
    return axios.delete(`/users/${id}`);
  };
  const deleteGroup = async (id: string) => {
    const response = await axios.delete(`/usergroups/${id}`);
    if (response.data === "OK")
      fetchUsers().then((users) => {
        users.forEach((user: User) => {
          if (user.ingroups.split(",").includes(id)) {
            const newInGroups = user.ingroups
              .split(",")
              .filter((groupId) => groupId !== id)
              .join(",");
            updateUser(user.id || "", { ingroups: newInGroups })
              .then((k) => {
                console.log("User removed successfully");
                return k;
              })
              .catch((e) => console.log("cannot remove user", e));
          }
        });
      });
    return response;
  };
  const validateNoAccessItems = (values: string[], items: string[]) => {
    return items.filter((item) => !values.includes(item));
  };
  const updateUserRights = async (
    id: string,
    values: FormikValues,
    deletedItems: string[],
    newGroup?: boolean
  ) => {
    try {
      const accesslvl0: string[] = [];
      const accesslvl1: string[] = [];
      const accesslvl2: string[] = [];
      await values.governmentEntities.map((item: any, i: number) => {
        if (item.accessLevel?.toString() === "0") {
          accesslvl0.push(item.id);
        } else if (item.accessLevel?.toString() === "1") {
          accesslvl1.push(item.id);
        } else if (item.accessLevel?.toString() === "2") {
          accesslvl2.push(item.id);
        }
        return true;
      });
      const mergedaccesslvl0 = newGroup
        ? accesslvl0
        : accesslvl0.concat(values.clusters, values.themes);
      if (newGroup) {
        if (geScoreCardId) {
          mergedaccesslvl0.push(geScoreCardId);
        }
        if (sectorScoreCardId) {
          mergedaccesslvl0.push(sectorScoreCardId);
        }
        if (themeScoreCardId) {
          mergedaccesslvl0.push(themeScoreCardId);
        }
      }
      const noAccess = newGroup
        ? validateNoAccessItems(
            [
              ...values.clusters,
              ...values.themes,
              ...accesslvl1,
              ...accesslvl2,
            ],
            values.noAccess
          )
        : [];
      const handleApi = async (item: any, i: number) => {
        if (item.length > 0)
          await axios.put(`/usergroups/${id}`, {
            accessLevelParameters: {
              objectIds: item.toString(),
              accessLevel: i.toString(),
            },
          });
      };
      [mergedaccesslvl0, accesslvl1, accesslvl2].forEach(handleApi);
      if (deletedItems.length)
        axios
          .put(`/usergroups/${id}`, {
            accessLevelParameters: {
              objectIds: deletedItems.join(","),
              removeAccess: true,
            },
          })
          .then(() => {
            handleApi([...noAccess, ...deletedItems], -1);
          });
    } catch (error) {
      console.log("Error", error);
    }
  };
  const getUserPersonaAndWfRoles = (
    roles: Role[],
    roleGroups: Group[]
  ): [RoleWithGroup[], RoleWithGroup[]] => {
    const ui: RoleWithGroup[] = [];
    const wf: RoleWithGroup[] = [];
    roles.forEach((role) => {
      const group = role.meaGroups[0];
      const roleGroup = roleGroups.find(({ name }) => name === group);
      const roleWithGroups = {
        ...role,
        name: userGroupsName[role.name] || role.name,
        meaGroup: roleGroup,
      };
      if (role.type === "ui") {
        ui.push(roleWithGroups);
      } else {
        wf.push(roleWithGroups);
      }
    });
    return [wf, ui];
  };
  const getUserPersonaAndWfRole = (
    roles: RoleWithGroup[],
    ingroups: string[]
  ) => {
    let ui = null;
    let wf = null;
    for (let index = 0; index < ingroups.length; index += 1) {
      const ingroup = ingroups[index];
      const role = roles.find((role) => role.meaGroup?.id === ingroup);
      if (role) {
        if (role?.type === "ui") {
          ui = role;
        } else {
          wf = role;
        }
        if (ui && wf) break;
      }
    }
    return { wf, ui };
  };
  return {
    createGroup,
    updateGroup,
    fetchUsersFromGroup,
    fetchuserGroup,
    fetchGroupsAndUsers,
    fetchUsers,
    createUser,
    fetchGroup,
    fetchUpAndWf,
    fetchRoleGroups,
    deleteGroup,
    deleteUser,
    updateUser,
    updateUserRights,
    getUserPersonaAndWfRoles,
    getUserPersonaAndWfRole,
  };
};
export default useUsermanagement;
